import React, { useMemo } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import Button from "@/components/Button/Button";
import Modal from "@/components/Modal/Modal";
import LoadingText from "@/components/LoadingText/LoadingText";
import { MODAL_ID } from "@/lib/constants";
import { getInitialValues, requiredMessage } from "@/lib/utils";
import { setModalOpen } from "@/store/slices/globalSlice";
import VendorIntegrationService from "@/services/api/vendorIntegration";
import formRenderer from "@/lib/formRenderer";
import toast from "@/lib/toast";
import styles from "./AddApiEndpoint.module.scss";

const INVOKE_VENDOR_API_ENDPOINTS_ON = {
  POST_ORDER_CREATION: { value: "POST_ORDER_CREATION", label: "Create Order" },
  FETCH_DELIVERY_STATUS: { value: "FETCH_DELIVERY_STATUS", label: "Get Delivery Status" },
  POLL_PRODUCT: { value: "POLL_PRODUCT", label: "Get Product" },
};

const invokeOnOptions = Object.values(INVOKE_VENDOR_API_ENDPOINTS_ON).map(
  (value) => ({
    value: value.value,
    label: value.label, 
  })
);

const AddApiEndpoint = ({ getData }) => {
  const dispatch = useDispatch();
  const { modalOpen } = useSelector((state) => state.global);

  const isOpen = modalOpen?.id === MODAL_ID.ADD_API_ENDPOINT;
  const { vendorIntegrationId, vendorId } = modalOpen?.data || {};

  const formConfig = useMemo(
    () => ({
      curl: {
        defaultValue: "",
        validations: {
          required: requiredMessage("Curl command"),
        },
        controlType: "textarea",
        label: "Curl Command",
        isRequired: true,
      },
      invokeOn: {
        defaultValue: null,
        validations: {
          required: requiredMessage("Invoke On"),
        },
        controlType: "select",
        label: "Invoke On",
        isRequired: true,
        isControlled: true,
        props: { options: invokeOnOptions },
      },
    }),
    []
  );

  const { register, control, handleSubmit, formState, reset } = useForm({
    defaultValues: getInitialValues(formConfig),
    mode: "onTouched",
  });

  const closeModal = () => {
    reset(getInitialValues(formConfig));
    dispatch(setModalOpen(false));
  };

  const onSubmit = async (values) => {
    const data = await VendorIntegrationService.addApiEndpoint({
      vendorIntegrationId,
      vendorId,
      curl: values?.curl,
      invokeOn: values?.invokeOn?.value,
    });
    if (data) {
      toast.success(data?.message || "API endpoint added successfully");
      getData?.();
      closeModal();
    }
  };

  return (
    <Modal modalOpen={isOpen} closeModal={closeModal} heading="Add API Endpoint">
      <form
        onSubmit={handleSubmit(onSubmit)}
        className={styles.form}
        autoComplete="off"
      >
        <div className={styles.main}>
          {formRenderer(formConfig, formState, register, control)}
        </div>
        <div className={styles.buttonsContainer}>
          <Button type="button" onClick={closeModal} outlined>
            Cancel
          </Button>
          <Button
            className={styles.submitButton}
            type="submit"
            disabled={formState.isSubmitting || !formState.isValid}
          >
            {formState.isSubmitting ? (
              <LoadingText text="Submitting" />
            ) : (
              "Add Endpoint"
            )}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default AddApiEndpoint;
